import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useViewTracker from "../../hooks/useViewTracker";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, Calendar, MapPin } from "lucide-react";
import { useData } from "../../context/DataContext";
import { formatCurrency } from "../../lib/format";
import SEO from "../../components/SEO";

const CITIES = ["All", "Cochin", "Trivandrum", "Calicut"];

const PreviewPackages = () => {
    const { packages } = useData();
    const { t } = useTranslation();
    useViewTracker("page", "/tour-packages");
    const [search, setSearch] = useSearchParams();
    const [city, setCity] = useState(
        CITIES.includes(search.get("city")) ? search.get("city") : "All",
    );
    const [sort, setSort] = useState("price-asc");

    const visible = useMemo(() => {
        let list = (packages || []).filter((p) => p.active);
        if (city !== "All") list = list.filter((p) => p.startingCity === city);
        list.sort((a, b) =>
            sort === "price-asc" ? a.price - b.price : b.price - a.price,
        );
        return list;
    }, [packages, city, sort]);

    const onCity = (c) => {
        setCity(c);
        setSearch(c === "All" ? {} : { city: c });
    };

    const serviceJsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Kerala Tour Packages",
        provider: {
            "@type": "TravelAgency",
            name: "SouthTrip.in by LuxSaCabs",
            url: "https://southtrip.in",
        },
        areaServed: { "@type": "Place", name: "Kerala, India" },
        serviceType: "Multi-day Kerala tour package with private cab and driver",
    };

    return (
        <div data-testid="preview-packages">
            <SEO
                title={t("seo.packagesTitle")}
                description={t("seo.packagesDescription")}
                path="/tour-packages"
                jsonLd={serviceJsonLd}
                keywords="kerala tour packages, kerala tour packages with driver, munnar tour, alleppey houseboat package, thekkady tour, wayanad tour, fort kochi tour, kovalam package"
            />
            <section className="max-w-7xl mx-auto px-6 pt-12 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <div className="hz-pill">Curated · {packages.length} routes</div>
                        <h1 className="font-hz-display text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-5">
                            {t("seo.packagesH1")}
                        </h1>
                    </div>
                    <div className="md:col-span-4">
                        <p className="text-[var(--ink-soft)] text-base leading-relaxed">
                            Multi-day Kerala itineraries from <b>Cochin</b>,{" "}
                            <b>Trivandrum</b> and <b>Calicut</b> — AC cab, verified driver,
                            fuel, tolls, parking all included.
                        </p>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 py-6 sticky top-[72px] z-20 bg-[var(--bg)] border-y border-[var(--border)]">
                <div className="flex items-center gap-3 flex-wrap text-sm">
                    <span className="text-[var(--ink-soft)] text-xs uppercase tracking-widest">From</span>
                    {CITIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => onCity(c)}
                            className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
                                city === c
                                    ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                                    : "border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--ink)]"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="ml-auto px-4 py-1.5 rounded-full border border-[var(--border)] text-xs font-semibold bg-white appearance-none pr-8"
                        style={{
                            backgroundImage:
                                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' stroke='%230F172A' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 12px center",
                        }}
                    >
                        <option value="price-asc">Low to high</option>
                        <option value="price-desc">High to low</option>
                    </select>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {visible.map((p, i) => {
                        const isFeature = i === 0;
                        return (
                            <Link
                                to={`/tour-packages/${p.id}`}
                                key={p.id}
                                className={`hz-card group overflow-hidden flex flex-col ${isFeature ? "md:col-span-2 md:row-span-2" : ""}`}
                                data-testid={`preview-pkg-${p.id}`}
                            >
                                <div className={`relative overflow-hidden bg-[var(--bg-surface)] ${isFeature ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
                                    <img src={p.image} alt={p.route} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                                    <div className="absolute top-4 left-4 flex gap-2">
                                        <span className="hz-pill bg-white/90">
                                            <MapPin className="w-3 h-3" /> {p.startingCity}
                                        </span>
                                        <span className="hz-pill" style={{ background: "var(--ink)", color: "#fff", border: "none" }}>
                                            {p.days}D · {p.nights}N
                                        </span>
                                    </div>
                                </div>
                                <div className={`${isFeature ? "p-8" : "p-6"} flex-1 flex flex-col`}>
                                    <h2 className={`font-hz-display leading-tight tracking-tight ${isFeature ? "text-3xl md:text-4xl" : "text-xl"}`}>
                                        {p.route}
                                    </h2>
                                    {p.highlight && (
                                        <div className="text-xs text-[var(--brand)] font-medium mt-2 uppercase tracking-widest">
                                            {p.highlight}
                                        </div>
                                    )}
                                    <div className="mt-auto pt-6 flex items-end justify-between">
                                        <div>
                                            <div className="text-[10px] text-[var(--ink-softer)] uppercase tracking-widest">From</div>
                                            <div className={`font-hz-display leading-none mt-1 ${isFeature ? "text-5xl" : "text-3xl"}`}>
                                                {formatCurrency(p.sedanPrice ?? p.price)}
                                            </div>
                                            <div className="text-[10px] text-[var(--ink-softer)] mt-1.5 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" /> {p.km} km
                                            </div>
                                        </div>
                                        <ArrowUpRight className="w-5 h-5 text-[var(--ink-soft)] group-hover:text-[var(--brand)] group-hover:rotate-45 transition" />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* ===== SEO LONG-FORM CONTENT ===== */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="packages-seo-content">
                <h2 className="font-hz-display text-3xl md:text-4xl tracking-tight">All-inclusive Kerala tour packages with cab and driver</h2>
                <p className="text-[var(--ink-soft)] mt-4 leading-relaxed">
                    SouthTrip Kerala tour packages bundle your entire holiday into one transparent price — private AC cab, Kerala-trained driver, fuel, tolls, parking and all inter-state permits. Itineraries are designed by local experts and battle-tested across more than 2,400 trips. Pick a 3-day Munnar escape from Cochin, a 5-day backwaters and tea-garden circuit, a 7-day Kerala honeymoon route or a 10-day grand South-India loop covering Goa, Mysore and Pondicherry. Every Kerala tour package starts and ends at the airport or railway station of your choice — Cochin (COK), Trivandrum (TRV) or Calicut (CCJ) — so you never have to figure out logistics yourself.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">Most-booked Kerala tour packages</h3>
                <ul className="text-[var(--ink-soft)] mt-3 space-y-2 list-disc pl-6 leading-relaxed">
                    <li><b>Cochin → Munnar → Alleppey</b> · 5 days, 4 nights — backwaters houseboat plus tea gardens, the classic Kerala first-timer route.</li>
                    <li><b>Trivandrum → Kovalam → Varkala → Kanyakumari</b> · 4 days — south Kerala beach circuit with sunrise at the southernmost tip of India.</li>
                    <li><b>Calicut → Wayanad → Bekal</b> · 4 days — north Kerala jungle, coffee estates and 17th-century forts.</li>
                    <li><b>Cochin → Munnar → Thekkady → Alleppey → Kovalam</b> · 8 days — full-Kerala loop combining hill stations, wildlife, backwaters and beach.</li>
                    <li><b>Cochin → Mysore → Coorg → Goa</b> · 10 days — South India grand tour with palaces, coffee plantations and Goan beaches.</li>
                    <li><b>Munnar honeymoon special</b> · 4 days — boutique tea-estate stay, private candlelit dinners and a houseboat overnight.</li>
                </ul>
                <h3 className="font-hz-display text-2xl mt-10">What is included in every Kerala tour package</h3>
                <ul className="text-[var(--ink-soft)] mt-3 space-y-2 list-disc pl-6 leading-relaxed">
                    <li>Private AC cab (sedan, SUV, tempo or mini-bus) for the entire duration of the tour.</li>
                    <li>Verified Kerala-trained chauffeur — English & Malayalam fluent, Hindi or Arabic on request.</li>
                    <li>Fuel, tolls, parking and inter-state permits to Tamil Nadu, Karnataka, Goa and Pondicherry.</li>
                    <li>Hand-vetted hotels and resorts (3-star family-run to 5-star heritage) — sea-view, hill-view or honeymoon rooms on request.</li>
                    <li>Licensed Kerala Tourism houseboat operator for the Alleppey overnight (premium and deluxe options).</li>
                    <li>Daily breakfast at all hotels; full-board on the houseboat; halal-certified meals on demand.</li>
                    <li>24/7 WhatsApp support before, during and after your trip — +91 80-75814740.</li>
                </ul>
                <h3 className="font-hz-display text-2xl mt-10">Why book a Kerala tour package with SouthTrip</h3>
                <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">
                    Every package gives you a private cab and verified driver — no shared coaches, no rushed group schedules, no hidden surcharges. Hotels are pre-vetted by our own team, houseboats are licensed Kerala Tourism operators, and our drivers double as your local guides for tea estates, spice plantations and Periyar wildlife safaris. Pay direct to the driver on arrival in cash, UPI or card — we will never ask for an online prepayment. A GST invoice is emailed immediately after every booking, which is especially useful for North-India clients (Delhi, Mumbai, Bangalore) flying to Cochin and Gulf clients arriving from Dubai, Doha, Abu Dhabi or Riyadh on direct Air India Express, IndiGo, Emirates, Qatar Airways or Etihad flights.
                </p>
                <h3 className="font-hz-display text-2xl mt-10">Customisable Kerala honeymoon and family tours</h3>
                <p className="text-[var(--ink-soft)] mt-3 leading-relaxed">
                    Honeymoon couples can add candlelit beach dinners, sunset cruises and tree-house stays at Wayanad or Munnar. Families travelling with children get child seats, kid-friendly restaurants and pre-booked halal options across Cochin, Munnar, Alleppey and Thekkady. Senior travellers receive ground-floor rooms and shorter daily drive durations on request. Need just a one-way <Link to="/airport-transfers" className="hz-link">Cochin or Trivandrum airport transfer</Link>, or a single-day <Link to="/cab-booking" className="hz-link">Kerala cab booking</Link> instead? Browse our <Link to="/destinations" className="hz-link">Kerala destinations</Link> guide for inspiration, or send a custom-itinerary brief through the <Link to="/contact" className="hz-link">contact</Link> page and we will reply within 4 working hours.
                </p>
            </section>
        </div>
    );
};

export default PreviewPackages;
